import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { fetchAuth } from "./api";
import { useTheme } from "./ThemeContext";
import { useSeason } from "./SeasonContext";
import { useTranslation } from "react-i18next";
import {
  storageSlotToInstant,
  instantToStorageSlot,
} from "./calendar/CalendarTimezone";

const PERIOD_LABEL = {
  morning: { short: "AM", long: "Morning ☀️" },
  afternoon: { short: "PM", long: "Afternoon 🌙" },
};

const typeIcon = (t) => {
  if (t === "match") return "⚽";
  if (t === "training") return "🏋️";
  if (t === "rest") return "😴";
  return "•";
};

const normalizeToList = (slot) => {
  if (!slot) return [];
  if (Array.isArray(slot)) return slot;
  if (typeof slot === "object") return [slot];
  if (typeof slot === "string") return [{ id: `legacy-${Math.random()}`, type: slot }];
  return [];
};

const summarizeEvents = (events) => {
  const list = normalizeToList(events);
  if (!list.length) return "—";

  const counts = { match: 0, training: 0, rest: 0 };
  for (const e of list) counts[e.type] = (counts[e.type] || 0) + 1;

  const parts = [];
  if (counts.match) parts.push(`⚽ ${counts.match}`);
  if (counts.training) parts.push(`🏋️ ${counts.training}`);
  if (counts.rest) parts.push(`😴 ${counts.rest}`);

  return parts.join("  ");
};

const getDefaultStartMin = (type) => {
  if (type === "match") return 15 * 60;
  if (type === "training") return 10 * 60;
  return 8 * 60;
};

const getDefaultDuration = (type) => {
  if (type === "match") return 105;
  if (type === "training") return 90;
  return 60;
};

const addMinutesLocal = (date, minutes) => {
  return new Date(date.getTime() + Number(minutes || 0) * 60_000);
};

const toLocalYmd = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const getLocalPeriod = (date) => {
  return date.getHours() < 12 ? "morning" : "afternoon";
};

const isValidDate = (value) => {
  return value instanceof Date && !Number.isNaN(value.getTime());
};

const parseLocalDateOnly = (value) => {
  if (isValidDate(value)) return value;

  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (match) {
      const [, y, m, d] = match;
      return new Date(Number(y), Number(m) - 1, Number(d));
    }

    const parsed = new Date(value);
    return isValidDate(parsed) ? parsed : null;
  }

  return null;
};

const formatDateLabel = (value, locale) => {
  const date = parseLocalDateOnly(value);
  if (!date) return "—";

  return date.toLocaleDateString(locale?.startsWith("fr") ? "fr-FR" : "en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
  });
};

const formatTimeLocal = (value) => {
  const date = parseLocalDateOnly(value);
  if (!date) return "--:--";

  return date.toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const convertUpcomingItemsToLocal = (items, limit) => {
  const now = new Date();
  const groups = new Map();

  for (const slot of items || []) {
    const events = normalizeToList(slot.events);

    for (const event of events) {
      const startMin = event?.start_min ?? getDefaultStartMin(event?.type);
      const durationMin = event?.duration_min ?? getDefaultDuration(event?.type);

      const localStart = storageSlotToInstant(slot.date, Number(startMin));

      if (!isValidDate(localStart)) {
        console.warn("[UpcomingActivities] Invalid localStart", {
          slotDate: slot.date,
          startMin,
          event,
        });
        continue;
      }

      const localEnd = addMinutesLocal(localStart, Number(durationMin));

      if (!isValidDate(localEnd)) {
        console.warn("[UpcomingActivities] Invalid localEnd", {
          localStart,
          durationMin,
          event,
        });
        continue;
      }

      // Évite d'afficher une activité déjà passée si l'API renvoie encore le slot du jour.
      if (localEnd < now) continue;

      const localDate = toLocalYmd(localStart);
      const localPeriod = getLocalPeriod(localStart);
      const key = `${localDate}-${localPeriod}`;

      if (!groups.has(key)) {
        groups.set(key, {
          date: localDate,
          period: localPeriod,
          localStart,
          events: [],
          has_player_customization: false,
          customized_players_count: 0,
        });
      }

      const group = groups.get(key);

      if (localStart < group.localStart) {
        group.localStart = localStart;
      }

      group.has_player_customization =
        group.has_player_customization || Boolean(slot.has_player_customization);

      group.customized_players_count = Math.max(
        Number(group.customized_players_count || 0),
        Number(slot.customized_players_count || 0)
      );

      group.events.push({
        ...event,
        _localStart: localStart,
        _localEnd: localEnd,
        _storageDate: slot.date,
        _storagePeriod: slot.period,
      });
    }
  }

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      events: group.events.sort((a, b) => a._localStart - b._localStart),
    }))
    .sort((a, b) => a.localStart - b.localStart)
    .slice(0, limit);
};

const renderDetailsLines = (events, t) => {
  const list = normalizeToList(events);

  return list.slice(0, 3).map((e) => {
    const timeLabel = e._localStart
      ? `${formatTimeLocal(e._localStart)}-${formatTimeLocal(e._localEnd)} · `
      : "";

    if (e.type === "match") {
      return `⚽ ${timeLabel}${t("calendar_ui.match")}${e?.opponent?.name ? ` vs ${e.opponent.name}` : ""}`;
    }

    if (e.type === "training") {
      return `🏋️ ${timeLabel}${t("calendar_ui.training")}${e?.label ? ` — ${e.label}` : ""}`;
    }

    if (e.type === "rest") {
      return `😴 ${timeLabel}${t("calendar_ui.rest")}`;
    }

    return `${typeIcon(e.type)} ${timeLabel}${e.type}`;
  });
};

const UpcomingActivities = ({ limit = 5 }) => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const { season } = useSeason();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  const fetchUpcoming = async () => {
    setLoading(true);

    try {
      const storageTodayStr = instantToStorageSlot(new Date()).date;

      // On demande un peu plus large parce que l'API limite avant conversion timezone.
      const apiLimit = Math.max(limit * 4, limit + 10);

      const res = await fetchAuth(
        `/calendar/upcoming?season=${encodeURIComponent(season)}&limit=${apiLimit}&from=${storageTodayStr}`
      );

      if (!res.ok) throw new Error("Failed to fetch upcoming activities");

      const data = await res.json();

      setItems(convertUpcomingItemsToLocal(data.items || [], limit));
    } catch (e) {
      toast.error("Error fetching upcoming");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpcoming();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season, limit]);

  return (
    <div
      className={`rounded-xl border p-4 shadow-sm ${
        isDarkMode ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-200 text-black"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold">{t("calendar_ui.upcoming_title")}</h3>
        <button
          onClick={fetchUpcoming}
          className={`text-sm px-3 py-1 rounded-md border ${
            isDarkMode
              ? "border-gray-700 bg-gray-800 hover:bg-gray-750"
              : "border-gray-200 bg-gray-50 hover:bg-gray-100"
          }`}
          disabled={loading}
          title={t("calendar_ui.upcoming_refresh")}
        >
          {loading ? "…" : "⟳"}
        </button>
      </div>

      {loading && items.length === 0 ? (
        <div className="text-sm opacity-70">{t("common.loading")}</div>
      ) : items.length === 0 ? (
        <div className="text-sm opacity-70">{t("calendar_ui.upcoming_empty")}</div>
      ) : (
        <div className="space-y-3">
          {items.map((it, idx) => {
            const periodMeta = {
              short: it.period === "morning" ? "AM" : it.period === "afternoon" ? "PM" : it.period,
              long: it.period === "morning"
                ? t("calendar_ui.morning")
                : it.period === "afternoon"
                  ? t("calendar_ui.afternoon")
                  : it.period,
            };
            const summary = summarizeEvents(it.events);
            const lines = renderDetailsLines(it.events, t);

            return (
              <div
                key={`${it.date}-${it.period}-${idx}`}
                className={`rounded-lg border p-3 ${
                  isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold">
                      {formatDateLabel(it.date, i18n.language)}{" "}
                      <span className="opacity-80">• {periodMeta.short}</span>
                    </div>
                    <div className="text-xs opacity-70 mt-0.5">{periodMeta.long}</div>
                  </div>

                  {it.has_player_customization ? (
                    <span
                      className={`text-xs px-2 py-1 rounded-full border ${
                        isDarkMode
                          ? "border-yellow-700 bg-yellow-900/30 text-yellow-200"
                          : "border-yellow-300 bg-yellow-50 text-yellow-800"
                      }`}
                      title={t("calendar_ui.upcoming_custom_title")}
                    >
                      ⭐ {t("calendar_ui.upcoming_custom", { count: it.customized_players_count ?? "?" })}
                    </span>
                  ) : (
                    <span
                      className={`text-xs px-2 py-1 rounded-full border opacity-70 ${
                        isDarkMode ? "border-gray-700" : "border-gray-200"
                      }`}
                      title={t("calendar_ui.upcoming_global_title")}
                    >
                      {t("pages.calendar.global")}
                    </span>
                  )}
                </div>

                <div className="mt-2 text-sm font-medium">{summary}</div>

                {lines.length > 0 && (
                  <ul className="mt-2 text-xs opacity-80 space-y-1">
                    {lines.map((l, i) => (
                      <li key={i} className="truncate">
                        {l}
                      </li>
                    ))}
                    {normalizeToList(it.events).length > 3 && (
                      <li className="opacity-60">{t("calendar_ui.upcoming_more", { count: normalizeToList(it.events).length - 3 })}</li>
                    )}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UpcomingActivities;
